import React, { useEffect, useState } from 'react';
import { Box, Link, CircularProgress } from '@mui/material';
import { DmProjectFile, getPublicSignedUrl } from '../api-services/dm-projects.client';

const ActionsFiles: React.FC<{ file: DmProjectFile, setOpenPdfUrl: (url: string) => void }> = ({ file, setOpenPdfUrl }) => {
    const [downloadUrl, setDownloadUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const fetchDownloadUrl = async () => {
            try {
                setIsLoading(true);
                setError('');
                const url = await getPublicSignedUrl(file.s3Key);
                setDownloadUrl(url);
            } catch (err) {
                console.error('Error fetching download URL:', err);
                setError('Failed to load file');
            } finally {
                setIsLoading(false);
            }
        };

        if (file?.s3Key) {
            fetchDownloadUrl();
        }
    }, [file.s3Key]);

    const isDisabled = isLoading || !!error || !downloadUrl;

    console.log("downloadUrl:", downloadUrl);

    const handleDownload = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (isDisabled) return;

        try {
            setIsLoading(true);
            // Fetch the file as a blob
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();

            // Create a blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl);
            setIsLoading(false);
        } catch (error) {
            console.error('Error downloading file:', error);
            // Fallback to direct link if fetch fails
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = file.name;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setIsLoading(false);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isLoading && <CircularProgress size={16} />}
            <Link
                href="#"
                underline="hover"
                sx={{
                    color: isDisabled ? '#ccc' : '#9C27B0',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    pointerEvents: isDisabled ? 'none' : 'auto',
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                }}
                onClick={handleDownload}
            >
                <svg width="18" height="18" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.9997 7.08333V3.75L17.833 9.58333L11.9997 15.4167V12C7.83301 12 4.91634 13.3333 2.83301 16.25C3.66634 12.0833 6.16634 7.91667 11.9997 7.08333Z" fill={isDisabled ? '#ccc' : '#9C27B0'} />
                </svg>
                {error ? 'Error' : 'Download'}
            </Link>
            {file?.fileType === 'application/pdf' && (
                <>
                    <span style={{ color: '#bbb' }}>|</span>
                    <Link
                        component="button"
                        underline="hover"
                        sx={{
                            color: isDisabled ? '#ccc' : '#9C27B0',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            pointerEvents: isDisabled ? 'none' : 'auto',
                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                        }}
                        onClick={() => !isDisabled && setOpenPdfUrl(downloadUrl)}
                        disabled={isDisabled}
                    >
                        <svg width="18" height="18" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5 4C5.5 4 2.16667 10 2.16667 10C2.16667 10 5.5 16 10.5 16C15.5 16 18.8333 10 18.8333 10C18.8333 10 15.5 4 10.5 4ZM10.5 14C8.01472 14 6 11.9853 6 9.5C6 7.01472 8.01472 5 10.5 5C12.9853 5 15 7.01472 15 9.5C15 11.9853 12.9853 14 10.5 14ZM10.5 7C9.11929 7 8 8.11929 8 9.5C8 10.8807 9.11929 12 10.5 12C11.8807 12 13 10.8807 13 9.5C13 8.11929 11.8807 7 10.5 7Z" fill={isDisabled ? '#ccc' : '#9C27B0'} />
                        </svg>
                        {error ? 'Error' : 'View'}
                    </Link>
                </>
            )}
        </Box>
    );
};

export default ActionsFiles; 